import { AssetData, VideoAssetData } from '@betterfunnel/types';
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from '@headlessui/react';
import {
  ChevronRightIcon,
  Cog6ToothIcon,
  PhotoIcon,
} from '@heroicons/react/24/outline';
import { PlayPauseIcon, TrashIcon } from '@heroicons/react/24/solid';
import * as Sentry from '@sentry/react';
import {
  ChangeEvent,
  DragEvent,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';
import { uid } from 'uid';
import { UserContext } from '../../../contexts/UserContext';
import ImageLibraryModal from '../../../modals/ImageLibraryModal/ImageLibraryModal';
import { classNames } from '../../../utils';
import { handleUpload } from '../../../utils/file-upload';
import Button from '../../Common/Button';
import IconButton from '../../Common/IconButton';
import Select from '../../Common/Select';
import ToggleButton from '../../Common/ToggleButton';
import Tooltip from '../../Common/Tooltip';

interface PropsMulti {
  image?: AssetData[];
  multi: true;
  onSelected: (images?: AssetData[]) => void;
  className?: string;
  types: ('image' | 'video')[];
}

interface PropsSingle {
  image?: AssetData;
  multi?: false;
  onSelected: (images?: AssetData) => void;
  className?: string;
  showImageSettings?:
    | boolean
    | { objectFit?: boolean; objectPosition?: boolean };
  types?: ('image' | 'video')[];
}

type Props = PropsMulti | PropsSingle;

export default function FileUploadInput({
  types = ['image'],
  ...props
}: Props) {
  const { image, multi, onSelected, className } = props;
  const id = uid();

  const { selectedAccount } = useContext(UserContext);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef<any>();
  const [selectImageModalOpen, setSelectImageModalOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  // handle drag events
  const handleDrag = function (e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleFileUpload = useCallback(
    (files: File[]) => {
      if (!selectedAccount) return;

      if (files.length) {
        setUploadProgress(0);
        handleUpload(selectedAccount.id, files, (progress) =>
          setUploadProgress(Math.ceil(progress)),
        )
          .then((res) => {
            setUploadProgress(null);
            onSelected(multi ? res : res[0]);
          })
          .catch((e) => {
            console.error(e);
            Sentry.captureException(e);
            setUploadProgress(null);
          });
      }
    },
    [selectedAccount, onSelected, multi],
  );

  const handleFileSelection = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files?.length ? Array.from(e.target.files) : [];
      handleFileUpload(files);

      e.target.value = '';
    },
    [handleFileUpload],
  );

  const handleFileDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();

      setDragActive(false);

      const files = e.dataTransfer.files?.length
        ? Array.from(e.dataTransfer.files)
        : [];
      handleFileUpload(files);

      e.dataTransfer.clearData();
    },
    [handleFileUpload],
  );

  return (
    <>
      <ImageLibraryModal
        open={selectImageModalOpen}
        onClose={() => setSelectImageModalOpen(false)}
        onSelected={(image) => {
          setSelectImageModalOpen(false);
          if (multi) onSelected(image);
          else onSelected(image?.[0]);
        }}
        types={types}
      />
      <div className="rounded-lg border border-dashed">
        <div
          onDragEnter={handleDrag}
          onDragOver={handleDrag}
          onDragLeave={handleDrag}
          onDrop={handleFileDrop}
          className={classNames(
            'h-40 w-full overflow-hidden',
            'relative flex min-h-[50%] justify-center rounded-lg border border-dashed border-gray-900/25',
            dragActive ? 'bg-blue-100' : '',
            className,
          )}
        >
          {(multi ? image?.length : image) ? (
            !multi ? (
              <div className="group relative w-full">
                {image && image?.type !== 'video' && (
                  <img
                    src={image?.url}
                    className="h-full w-full rounded-lg object-contain"
                    onClick={() => setSelectImageModalOpen(true)}
                  />
                )}
                {image && image?.type === 'video' && (
                  <div className="relative h-full w-full">
                    <video
                      id={`video-${id}`}
                      src={image?.url}
                      controls={false}
                      className="pointer-events-none -z-10 h-full w-full object-contain"
                    ></video>
                    <IconButton
                      color="positive"
                      className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
                      onClick={() => {
                        const video = document.querySelector(
                          `#video-${id}`,
                        ) as HTMLVideoElement;
                        if (video) {
                          video.paused ? video.play() : video.pause();
                        }
                      }}
                    >
                      <PlayPauseIcon className="h-8 w-8" />
                    </IconButton>
                  </div>
                )}
                <div className="pointer-events-none invisible absolute bottom-0 flex w-full items-stretch gap-2 rounded-b-lg bg-white/30 p-2 backdrop-blur-lg group-hover:visible">
                  <Button
                    className="pointer-events-auto"
                    onClick={() => setSelectImageModalOpen(true)}
                  >
                    Replace
                  </Button>
                  {!props.multi && props.showImageSettings && (
                    <Button
                      className="pointer-events-auto"
                      color="positive"
                      size="small"
                      onClick={() => setShowSettings(!showSettings)}
                    >
                      <Cog6ToothIcon className="h-5 w-5" />
                    </Button>
                  )}
                  <div className="flex-1"></div>
                  <Button
                    className="pointer-events-auto"
                    color="negative"
                    size="small"
                    onClick={() => onSelected(undefined)}
                  >
                    <TrashIcon className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ) : (
              <>{/* not supported */}</>
            )
          ) : (
            <div
              className={classNames(
                'flex flex-col justify-center py-10 text-center',
                uploadProgress !== null ? 'pointer-events-none opacity-0' : '',
              )}
            >
              <PhotoIcon
                className="mx-auto h-12 w-12 text-gray-300"
                aria-hidden="true"
              />
              <div className="mt-4 flex whitespace-nowrap text-sm leading-6 text-gray-600">
                <label
                  // htmlFor={'file-upload' + id}
                  className="relative cursor-pointer rounded-md font-semibold text-indigo-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-indigo-600 focus-within:ring-offset-2 hover:text-indigo-500"
                  onClick={() => setSelectImageModalOpen(true)}
                >
                  <span>Select {multi ? 'files' : 'a file'}</span>
                </label>
                <p className="pl-1">or drag and drop</p>
              </div>
              <p className="text-xs leading-5 text-gray-600">
                PNG, JPG, GIF up to 10MB
              </p>
            </div>
          )}
          <input
            id={'file-upload' + id}
            name={'file-upload' + id}
            ref={inputRef}
            type="file"
            multiple={multi}
            accept="image/*"
            className="sr-only"
            onChange={handleFileSelection}
          />
          {uploadProgress !== null ? (
            <div className="absolute w-full">
              <UploadProgressBar progress={uploadProgress} />
            </div>
          ) : (
            <></>
          )}
        </div>
        {showSettings && !props.multi && props.showImageSettings && (
          <AssetSettings
            image={props.image}
            showImageSettings={props.showImageSettings}
            onSelected={props.onSelected}
          />
        )}
      </div>
    </>
  );
}

const UploadProgressBar = ({ progress }: { progress: number }) => {
  return (
    <div className="w-full overflow-hidden rounded-t-lg bg-gray-200">
      <div
        className="min-w-[20px] rounded-ss-lg bg-blue-600 p-0.5 text-center text-xs font-medium leading-none text-blue-100 transition-all"
        style={{ width: progress + '%' }}
      >
        {progress}%
      </div>
    </div>
  );
};

const AssetSettings = (props: {
  image?: AssetData;
  showImageSettings: PropsSingle['showImageSettings'];
  onSelected: PropsSingle['onSelected'];
}) => {
  if (!props.showImageSettings) return null;

  const { image } = props;

  return (
    <div className="flex flex-col gap-2 p-2">
      {image?.type !== 'video' && (
        <>
          {(props.showImageSettings === true ||
            props.showImageSettings.objectFit) && (
            <div>
              <label className="mb-2 block text-sm font-medium leading-6 text-gray-900">
                Image Settings
              </label>
              <Tooltip text={'Add an image'} disabled={!!props.image}>
                <ToggleButton<AssetData['objectFit']>
                  disabled={!props.image}
                  value={props.image?.objectFit ?? 'default'}
                  items={[
                    { value: 'default', title: 'Default' },
                    { value: 'cover', title: 'Cover' },
                    { value: 'contain', title: 'Contain' },
                    { value: 'fill', title: 'Fill' },
                  ]}
                  onChange={(selected) => {
                    if (!props.image) return;

                    if (!selected || selected === 'default') {
                      delete props.image.objectFit;
                    } else {
                      props.image.objectFit = selected;
                    }

                    props.onSelected(props.image);
                  }}
                />
              </Tooltip>
            </div>
          )}
          {(props.showImageSettings === true ||
            props.showImageSettings.objectPosition) && (
            <div>
              <Tooltip text={'Add an image'} disabled={!!props.image}>
                <Select<AssetData['objectPosition']>
                  disabled={!props.image}
                  selected={props.image?.objectPosition ?? 'default'}
                  options={[
                    {
                      value: 'default',
                      child: (
                        <>
                          <span className="me-2 text-xs">Position:</span>
                          default
                        </>
                      ),
                    },
                    {
                      value: 'left',
                      child: (
                        <>
                          <span className="me-2 text-xs">Position:</span>
                          left
                        </>
                      ),
                    },
                    {
                      value: 'center',
                      child: (
                        <>
                          <span className="me-2 text-xs">Position:</span>
                          center
                        </>
                      ),
                    },
                    {
                      value: 'right',
                      child: (
                        <>
                          <span className="me-2 text-xs">Position:</span>
                          right
                        </>
                      ),
                    },
                    {
                      value: 'top',
                      child: (
                        <>
                          <span className="me-2 text-xs">Position:</span>
                          top
                        </>
                      ),
                    },
                    {
                      value: 'bottom',
                      child: (
                        <>
                          <span className="me-2 text-xs">Position:</span>
                          bottom
                        </>
                      ),
                    },
                    {
                      value: 'left top',
                      child: (
                        <>
                          <span className="me-2 text-xs">Position:</span>
                          left top
                        </>
                      ),
                    },
                    {
                      value: 'left bottom',
                      child: (
                        <>
                          <span className="me-2 text-xs">Position:</span>
                          left bottom
                        </>
                      ),
                    },
                    {
                      value: 'right top',
                      child: (
                        <>
                          <span className="me-2 text-xs">Position:</span>
                          right top
                        </>
                      ),
                    },
                    {
                      value: 'right bottom',
                      child: (
                        <>
                          <span className="me-2 text-xs">Position:</span>
                          right bottom
                        </>
                      ),
                    },
                  ]}
                  onChange={(selected) => {
                    if (!props.image) return;
                    if (!selected || selected === 'default') {
                      delete props.image.objectPosition;
                    } else {
                      props.image.objectPosition = selected;
                    }
                    props.onSelected(props.image);
                  }}
                />
              </Tooltip>
            </div>
          )}
        </>
      )}
      {image && image?.type === 'video' ? (
        <>
          <Disclosure as="div" className="mt-1">
            {({ open }) => (
              <>
                <DisclosureButton className="mb-2 flex w-full justify-between text-sm font-medium leading-6 text-gray-900">
                  <span>Video Thumbnail</span>
                  <ChevronRightIcon
                    className={`${open ? 'rotate-90 transform' : ''} h-5 w-5`}
                  />
                </DisclosureButton>
                <DisclosurePanel>
                  <FileUploadInput
                    onSelected={(newImage) => {
                      if (!image) return;
                      if (image.type !== 'video') return;

                      if (newImage && newImage.type === 'image') {
                        image.thumbnailImage = newImage;
                      } else {
                        delete image.thumbnailImage;
                      }
                      props.onSelected(image);
                    }}
                    image={image?.thumbnailImage}
                    types={['image']}
                    showImageSettings
                  />
                </DisclosurePanel>
              </>
            )}
          </Disclosure>
          <Select<VideoAssetData['openIn'] | 'none'>
            selected={image?.openIn ?? 'none'}
            onChange={(selected) => {
              if (!image) return;
              if (selected === 'none') {
                delete image.openIn;
              }
              if (selected === 'modal') {
                image.openIn = 'modal';
              }
              if (selected === 'hover') {
                image.openIn = 'hover';
              }
              if (selected === 'autoplay') {
                image.openIn = 'autoplay';
              }
              props.onSelected(image);
            }}
            options={[
              { child: 'Open on view', value: 'none' },
              {
                child: 'Open in modal',
                value: 'modal',
              },
              {
                child: 'Open on hover',
                value: 'hover',
              },
              {
                child: 'Autoplay',
                value: 'autoplay',
              },
            ]}
          />
        </>
      ) : (
        <></>
      )}
    </div>
  );
};
